import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from '../../contexts/translation-context';
import { useConfig } from '../../contexts/config-context';
import Fieldset from '../fieldset';
import { DayField } from './day-field';
import { MonthSelect } from './month-select';
import { YearField } from './year-field';
import { TimePicker } from './time-picker';
import { deserialize, serialize, isDateTimeFieldElement } from './helpers';
import { PlaceholderType } from './types';
import styles from './date-time-field.scss';
export function DateTimeField(props) {
  const {
    showTimeInput = false,
    use24HourFormat = false,
    error,
    onBlur,
    onChange,
    onFocus,
    inputId,
    ariaLabelledBy,
    placeholder = PlaceholderType.Normal,
    dateFormatParts = ['day', 'month', 'year']
  } = props;
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useConfig();
  const value = useMemo(() => deserialize(props.value), [props.value]);
  const placeholders = useMemo(() => {
    switch (placeholder) {
      case PlaceholderType.None:
        return {};
      case PlaceholderType.Normal:
      default:
        return {
          day: t('field.date.placeholder.day'),
          month: t('field.date.placeholder.month'),
          year: t('field.date.placeholder.year'),
          time: t('field.date.placeholder.time')
        };
    }
  }, [placeholder, t]);
  const _onChange = (newValue, datePart) => {
    onChange(serialize(newValue, datePart, value));
  };
  return /*#__PURE__*/React.createElement(Fieldset, {
    id: inputId,
    onFocus: onFocus,
    onBlur: handleBlur,
    ariaLabelledBy: ariaLabelledBy,
    className: classNames(styles.fields, {
      [styles.mobile]: isMobile
    })
  }, dateFormatParts.map(getDatePart), showTimeInput && /*#__PURE__*/React.createElement(TimePicker, {
    id: props.id,
    error: error,
    value: value.time,
    military: use24HourFormat,
    className: styles.time,
    disabled: props.disabled,
    required: props.required,
    "data-hook": props.id + '-time',
    placeholder: placeholders.time,
    onChange: newValue => _onChange(newValue, 'time')
  }));
  function handleBlur(event) {
    if (!isDateTimeFieldElement(event.relatedTarget)) {
      onBlur == null || onBlur(event);
    }
  }
  function getDatePart(type) {
    switch (type) {
      case 'day':
        return /*#__PURE__*/React.createElement(DayField, {
          id: props.id,
          error: error,
          value: value.day,
          disabled: props.disabled,
          className: styles.day,
          required: props.required,
          placeholder: placeholders.day,
          "data-date-time-field": true,
          "data-hook": props.id + '-day',
          onChange: newValue => _onChange(newValue, 'day')
        });
      case 'month':
        return /*#__PURE__*/React.createElement(MonthSelect, {
          id: props.id,
          error: error,
          value: value.month,
          disabled: props.disabled,
          className: styles.month,
          required: props.required,
          "data-date-time-field": true,
          "data-hook": props.id + '-month',
          placeholder: placeholders.month,
          onChange: newValue => _onChange(newValue, 'month')
        });
      case 'year':
        return /*#__PURE__*/React.createElement(YearField, {
          id: props.id,
          error: error,
          value: value.year,
          className: styles.year,
          disabled: props.disabled,
          required: props.required,
          placeholder: placeholders.year,
          "data-hook": props.id + '-year',
          "data-date-time-field": true,
          onChange: newValue => _onChange(newValue, 'year')
        });
      default:
        return null;
    }
  }
}
DateTimeField.displayName = 'DateTimeField';
//# sourceMappingURL=date-time-field.js.map